/*──────────────────────────────────────────────────────────────
 * src/components/UsersTab.jsx — fully regenerated 2026-01-06
 * • Visible Edit button per row (matches original UX)
 * • JobRole works end-to-end (assign on user modal; tenant JobRole editor)
 * • Pulls UNIQUE JobRoles from JobTypes via clientDetails.php?jobRoles=1 (Active-only on server)
 * • Hides JobRole UI if no jobTypes / UseFieldTicket disabled
 * • Compact, safe, and fully self-contained
 * • IMPORTANT: JobRole Editor uses mode=REPLACE so unchecking removes on server/state.
 * • 2025-11-05: Per-user JobRole selection uses inline checkbox chips with search
 *               and “Select All / Clear”. (No quick-add, no JobType association here.)
 * • 2025-12-24: Hide OgPumper read/write perms UI + summary (not shown to customers for now).
 *               Include Role value R = "Read Only".
 * • 2026-01-06: FIX create-with-existing-UserID bug:
 *               - Create flow will NOT PATCH/overwrite an existing record.
 *               - If UserID already exists, prompt: “Modify existing?” and switch to Edit.
 *               - Edit flow PATCH always targets original UserID; UserID field locked in Edit.
 *──────────────────────────────────────────────────────────────*/

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Modal from "react-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faUserEdit,
  faTimes,
  faSortUp,
  faSortDown,
  faSearch,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons";

/* react-modal a11y */
if (typeof document !== "undefined") {
  try {
    Modal.setAppElement("#root");
  } catch (_) {}
}

/* ───────────── constants ───────────── */
const ROLE_DESCRIPTIONS = {
  P: "Pumper",
  O: "Operator",
  I: "Investor",
  A: "Admin",
  R: "Read Only",
};

const DEFAULT_PERMS = {
  ogfieldticket: { read: true, write: true },
  ogpumper: { read: true, write: true },
};

/* badge palette */
const badgeBase =
  "inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap";
const badgeStyles = {
  FT: "bg-blue-600 text-white",
};

/* API helper */
const JOBROLE_ENDPOINT = (company) =>
  `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
    company || ""
  )}&jobRoles=1`;

/* Helpers: JobRole ←→ string */
const splitRoles = (str) =>
  (str || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const joinRoles = (arr) =>
  Array.from(
    new Set((arr || []).map((s) => String(s).trim()).filter(Boolean))
  ).join(",");

/* ───────────── helpers ───────────── */
const getInputClasses = (th) =>
  `w-full border px-1 py-1 text-sm rounded focus:outline-none focus:ring-2 focus:ring-blue-400 ${
    th === "dark"
      ? "bg-gray-700 text-gray-100 border-gray-600"
      : "border-gray-300"
  }`;

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

function normalizePerms(p) {
  let obj = {};
  if (!p) obj = {};
  else if (typeof p === "string") {
    try {
      obj = JSON.parse(p);
    } catch {
      obj = {};
    }
  } else obj = { ...p };
  if (obj.fieldticket && !obj.ogfieldticket)
    obj.ogfieldticket = obj.fieldticket;
  delete obj.fieldticket;
  return {
    ogfieldticket: { ...DEFAULT_PERMS.ogfieldticket, ...obj.ogfieldticket },
    ogpumper: { ...DEFAULT_PERMS.ogpumper, ...obj.ogpumper },
  };
}

const formatPermsSummary = (perms) => {
  const p = normalizePerms(perms);
  // NOTE: Hide OgPumper perms for now (customer-facing)
  return [
    `<span class="${badgeBase} ${badgeStyles.FT}">FT&nbsp;${
      p.ogfieldticket.read ? "R" : ""
    }${p.ogfieldticket.write ? "/W" : ""}</span>`,
  ].join(" ");
};

/* ───────────── modal inline styles (fix invisible modal) ───────────── */
const modalStyles = {
  overlay: {
    zIndex: 1000,
    backgroundColor: "rgba(0,0,0,0.55)",
  },
  content: {
    inset: "10% 10%",
    border: "none",
    borderRadius: "12px",
    padding: 0,
    overflow: "auto",
    background: "transparent",
  },
};

/* Small chip checkbox component */
function Chip({ checked, label, onChange, theme }) {
  return (
    <label
      className={`cursor-pointer select-none inline-flex items-center px-2 py-1 mr-2 mb-2 rounded-full border text-xs ${
        checked
          ? "bg-blue-600 text-white border-blue-600"
          : theme === "dark"
          ? "bg-gray-700 text-gray-100 border-gray-500 hover:bg-gray-600"
          : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100"
      }`}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

/* ───────────── component ───────────── */
function UsersTab({
  users = [],
  setUsers,
  theme,
  companyName,
  role,
  useFieldTicket,
  jobTypes = [],
  baseUrl = "https://house.ogendeavors.com", // for PATCHes (tenant editor)
  onRefreshJobTypes, // optional parent refresher (tenant editor)
}) {
  /* ───────────── ui state ───────────── */
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Explicit mode prevents "create w/ existing ID" from turning into PATCH overwrite
  const [userModalMode, setUserModalMode] = useState("create"); // "create" | "edit"
  const [originalUserId, setOriginalUserId] = useState(""); // PATCH target for edit mode

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("UserID");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showDisabled, setShowDisabled] = useState(false);

  /* Tenant JobRole editor modal */
  const [isJobRoleModalOpen, setIsJobRoleModalOpen] = useState(false);
  const [jobRoles, setJobRoles] = useState([]); // UNIQUE roles from tenant (Active-only server logic)
  const [selectedJobRoleIds, setSelectedJobRoleIds] = useState([]); // strings
  const [newJobRoleName, setNewJobRoleName] = useState("");
  const [selectedJobTypeId, setSelectedJobTypeId] = useState(""); // modal selector

  /* Local mirror of jobTypes to prevent stale UI if parent doesn’t refresh immediately */
  const [localJobTypes, setLocalJobTypes] = useState(jobTypes);
  useEffect(() => setLocalJobTypes(jobTypes), [jobTypes]);

  /* Template */
  const blankUser = {
    UserID: "",
    Sec: "",
    Role: "P",
    FullName: "",
    Email: "",
    Phone: "",
    GroupID: "",
    Message: "",
    Address: "",
    CityStateZip: "",
    Misc: "",
    AutoAdvance: "C",
    google_id: "",
    ChartsPref: "",
    Disabled: "0",
    JobRole: "",
    perms: { ...DEFAULT_PERMS },
  };

  /* Whether to show ANY JobRole UI: only if FT on AND jobTypes available */
  const showJobRoleUI = useFieldTicket === "Y";

  /* loaders */
  const fetchJobRoles = async () => {
    if (!companyName || !showJobRoleUI) {
      setJobRoles([]);
      return;
    }
    try {
      const resp = await fetch(JOBROLE_ENDPOINT(companyName));
      const data = await resp.json();
      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.jobRoles)
        ? data.jobRoles
        : [];
      const uniq = Array.from(
        new Set(arr.map((r) => String(r).trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
      setJobRoles(uniq);
    } catch (e) {
      console.error("Failed to load tenant JobRoles:", e);
      setJobRoles([]);
    }
  };

  const fetchTicketTypes = async () => {
    if (typeof onRefreshJobTypes === "function") {
      try {
        await onRefreshJobTypes();
      } catch (e) {
        console.error(e);
      }
      return;
    }
    // Optional: wire a jobs listing endpoint here if you maintain jobTypes locally.
  };

  /* Seed defaults + pull roles */
  useEffect(() => {
    if (!showJobRoleUI) {
      setJobRoles([]);
      setSelectedJobTypeId("");
      return;
    }
    const firstJT = localJobTypes?.[0]?.JobTypeID
      ? String(localJobTypes[0].JobTypeID)
      : "";
    if (!selectedJobTypeId) setSelectedJobTypeId(firstJT);
    fetchJobRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, showJobRoleUI, localJobTypes]);

  /* When a JobType is selected in the JobRole editor, pre-check its roles and inject them into the list if missing */
  useEffect(() => {
    if (!isJobRoleModalOpen || !selectedJobTypeId) return;
    const jt = localJobTypes.find(
      (j) => String(j.JobTypeID) === String(selectedJobTypeId)
    );
    const rolesOnJT = splitRoles(jt?.JobRole || "");
    if (rolesOnJT.length) {
      setJobRoles((prev) => {
        const set = new Set(prev);
        rolesOnJT.forEach((r) => set.add(r));
        return Array.from(set).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        );
      });
      setSelectedJobRoleIds(rolesOnJT);
    } else {
      setSelectedJobRoleIds([]);
    }
  }, [isJobRoleModalOpen, selectedJobTypeId, localJobTypes]);

  /* Also re-sync selections if jobTypes prop refreshes while modal is open */
  useEffect(() => {
    if (!isJobRoleModalOpen || !selectedJobTypeId) return;
    const jt = localJobTypes.find(
      (j) => String(j.JobTypeID) === String(selectedJobTypeId)
    );
    const rolesOnJT = splitRoles(jt?.JobRole || "");
    setSelectedJobRoleIds(rolesOnJT);
  }, [localJobTypes, isJobRoleModalOpen, selectedJobTypeId]);

  /* Per-user JobRole options come from jobRoles list */
  const jobRoleOptions = useMemo(
    () => (showJobRoleUI ? jobRoles : []),
    [showJobRoleUI, jobRoles]
  );

  /* ─────────── CRUD helpers ─────────── */
  const handleCreateUser = () => {
    setUserModalMode("create");
    setOriginalUserId("");
    setRoleChipSearch("");
    setEditingUser({ ...blankUser });
    setShowUserModal(true);
  };

  const handleEditClick = (u) => {
    setUserModalMode("edit");
    setOriginalUserId(String(u?.UserID || ""));
    setRoleChipSearch("");
    setEditingUser({ ...u, perms: normalizePerms(u.perms) });
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    for (const [k, lbl] of [
      ["UserID", "User ID"],
      ["Sec", "Password"],
      ["FullName", "Full Name"],
      ["Role", "Role"],
    ]) {
      if (!(editingUser[k] ?? "").trim()) {
        alert(`${lbl} is required.`);
        return;
      }
    }

    const enteredUserId = String(editingUser.UserID || "").trim();
    const existingLocal = users.find(
      (u) =>
        String(u.UserID || "").toLowerCase() === enteredUserId.toLowerCase()
    );

    // HARD GUARD: If in CREATE mode and the ID already exists, NEVER PATCH/overwrite.
    // Instead prompt to switch to edit.
    if (userModalMode === "create" && existingLocal) {
      const goEdit = window.confirm(
        `UserID "${existingLocal.UserID}" already exists.\n\nDo you want to modify the existing user instead?`
      );
      if (goEdit) {
        // Switch to edit mode and load the existing record (authoritative from current list)
        setUserModalMode("edit");
        setOriginalUserId(String(existingLocal.UserID || ""));
        setEditingUser({
          ...existingLocal,
          perms: normalizePerms(existingLocal.perms),
        });
      } else {
        alert(
          `Create cancelled.\n\nChoose a different UserID to create a NEW user.`
        );
      }
      return;
    }

    const jr = joinRoles(splitRoles(editingUser.JobRole || ""));

    const permsPayload = {
      ogfieldticket: editingUser.perms.ogfieldticket,
      ogpumper: editingUser.perms.ogpumper, // still preserved/sent even though UI is hidden
    };
    const payload = Object.fromEntries(
      Object.entries({ ...editingUser, JobRole: jr }).map(([k, v]) => {
        if (k === "perms") return [k, JSON.stringify(permsPayload)];
        return [k, typeof v === "string" ? v.trim() : v];
      })
    );

    const urlBase = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
      companyName
    )}`;

    const isCreate = userModalMode === "create";
    const patchTargetUserId = String(
      originalUserId || editingUser.UserID || ""
    ).trim();

    const url = isCreate
      ? `${urlBase}&createUser=1`
      : `${urlBase}&uid=${encodeURIComponent(patchTargetUserId)}`;

    const method = isCreate ? "POST" : "PATCH";

    try {
      /* 1) Save the user */
      const resp = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ users: [payload] }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || "Unknown server error");

      const listUser = { ...editingUser, JobRole: jr };

      if (isCreate) {
        setUsers([...users, listUser]);
      } else {
        // Update the user we *actually* edited (originalUserId), not "whatever matches current UserID"
        const idx = users.findIndex(
          (u) => String(u.UserID || "") === String(patchTargetUserId)
        );
        if (idx >= 0) {
          const updated = [...users];
          updated[idx] = listUser;
          setUsers(updated);
        } else {
          // Fallback: if list is stale, try matching current ID, else append
          const idx2 = users.findIndex(
            (u) => String(u.UserID || "") === String(listUser.UserID || "")
          );
          if (idx2 >= 0) {
            const updated = [...users];
            updated[idx2] = listUser;
            setUsers(updated);
          } else {
            setUsers([...users, listUser]);
          }
        }
      }
    } catch (err) {
      console.error("Error creating/patching user:", err);
      alert("Save failed.");
    } finally {
      setShowUserModal(false);
      setEditingUser(null);
      setUserModalMode("create");
      setOriginalUserId("");
    }
  };

  const handleCancelUser = () => {
    setEditingUser(null);
    setShowUserModal(false);
    setUserModalMode("create");
    setOriginalUserId("");
  };

  const handleModalFieldChange = (e) => {
    const { name, value } = e.target;

    // Prevent accidental PK changes in edit mode (PATCH targets original user id)
    if (name === "UserID" && userModalMode === "edit") return;

    setEditingUser((p) => ({ ...p, [name]: value }));
  };

  const handleDisabledToggle = (checked) => {
    setEditingUser((p) => ({ ...p, Disabled: checked ? "1" : "0" }));
  };

  const handlePermCheckbox = (app, key, checked) => {
    setEditingUser((p) => ({
      ...p,
      perms: { ...p.perms, [app]: { ...p.perms[app], [key]: checked } },
    }));
  };

  /* search/sort */
  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleSort = (key) =>
    sortBy === key
      ? setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
      : (setSortBy(key), setSortDirection("asc"));

  /* filtered users (hide disabled unless toggled on) */
  const filteredUsers = useMemo(() => {
    const base = users.filter((u) => {
      const uid = (u.UserID || "").toLowerCase();
      const name = (u.FullName || "").toLowerCase();
      if (uid === "admin" || name === "admin") return false;
      if (!showDisabled && String(u.Disabled) === "1") return false;
      return true;
    });

    const term = searchTerm.toLowerCase();
    const result = base.filter((u) => {
      const roleDesc = ROLE_DESCRIPTIONS[u.Role] || "";
      const jr = (u.JobRole || "").toLowerCase();
      return (
        (u.UserID || "").toLowerCase().includes(term) ||
        (u.FullName || "").toLowerCase().includes(term) ||
        (u.Email || "").toLowerCase().includes(term) ||
        roleDesc.toLowerCase().includes(term) ||
        (u.Role || "").toLowerCase().includes(term) ||
        jr.includes(term)
      );
    });

    return result.sort((a, b) => {
      const av = a[sortBy] ?? "";
      const bv = b[sortBy] ?? "";
      const res = collator.compare(String(av), String(bv));
      return sortDirection === "asc" ? res : -res;
    });
  }, [users, searchTerm, sortBy, sortDirection, showDisabled]);

  /* CSV export */
  const handleExportUsers = () => {
    if (!filteredUsers.length) return;
    const includeJR = showJobRoleUI;
    const hdr = includeJR
      ? ["UserID", "FullName", "Email", "Role", "JobRole", "Disabled", "Perms"]
      : ["UserID", "FullName", "Email", "Role", "Disabled", "Perms"];
    const rows = filteredUsers.map((u) => {
      const row = [
        u.UserID,
        u.FullName,
        u.Email,
        ROLE_DESCRIPTIONS[u.Role] || u.Role,
      ];
      if (includeJR) row.push(joinRoles(splitRoles(u.JobRole || "")));
      row.push(String(u.Disabled) === "1" ? "Yes" : "No");
      row.push(formatPermsSummary(u.perms).replace(/<[^>]+>/g, ""));
      return row;
    });
    const csv = [hdr, ...rows]
      .map((r) =>
        r.map((f) => `"${String(f ?? "").replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* Helpers for easier per-user JobRole chip UX */
  const userRolesArr = (editingUser && splitRoles(editingUser.JobRole)) || [];
  const setUserRolesArr = (arr) =>
    setEditingUser((p) => ({ ...p, JobRole: joinRoles(arr) }));

  const [roleChipSearch, setRoleChipSearch] = useState("");
  const filteredRoleChips = useMemo(() => {
    const q = roleChipSearch.trim().toLowerCase();
    const list = jobRoleOptions;
    if (!q) return list;
    return list.filter((r) => r.toLowerCase().includes(q));
  }, [jobRoleOptions, roleChipSearch]);

  return (
    <div className="p-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-2">
        {/* Search */}
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className={`absolute left-3 top-2 ${
              theme === "dark" ? "text-gray-400" : "text-gray-500"
            }`}
          />
          <input
            type="text"
            placeholder="Search users…"
            value={searchTerm}
            onChange={handleSearchChange}
            className={`pl-9 pr-3 py-1 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              theme === "dark"
                ? "bg-gray-700 text-gray-100 border border-gray-600"
                : "bg-white text-gray-700 border border-gray-300"
            }`}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap items-center">
          <button
            onClick={handleCreateUser}
            className="flex items-center font-bold py-1 px-3 rounded-md shadow-sm bg-blue-500 text-white hover:bg-blue-600"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add New User
          </button>

          <button
            onClick={handleExportUsers}
            className="flex items-center font-bold py-1 px-3 rounded-md shadow-sm bg-green-500 text-white hover:bg-green-600"
          >
            <FontAwesomeIcon icon={faFileExport} className="mr-2" />
            Export
          </button>

          {/* Show/Hide Disabled */}
          <label className="flex items-center text-sm font-semibold ml-2">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
              className="mr-1"
            />
            Show Disabled
          </label>

          {/* Tenant JobRole editor shortcut */}
          {showJobRoleUI && (
            <button
              onClick={async () => {
                setSelectedJobRoleIds([]);
                setNewJobRoleName("");
                const firstJT = localJobTypes?.[0]?.JobTypeID
                  ? String(localJobTypes[0].JobTypeID)
                  : "";
                if (!selectedJobTypeId) setSelectedJobTypeId(firstJT);
                await fetchJobRoles(); // ensure roles are present before opening
                setIsJobRoleModalOpen(true);
              }}
              className="text-xs underline text-blue-600 hover:text-blue-700"
              title="Edit tenant Job Roles"
            >
              Edit Job Roles
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {users && users.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead
              className={`border-b ${
                theme === "dark"
                  ? "bg-gray-700 text-gray-100 border-gray-600"
                  : "bg-gray-100 text-gray-800 border-gray-200"
              }`}
            >
              <tr>
                <SortableHeader
                  label="UserID"
                  columnKey="UserID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Full Name"
                  columnKey="FullName"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Email"
                  columnKey="Email"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Role"
                  columnKey="Role"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                {showJobRoleUI && (
                  <SortableHeader
                    label="Job Role(s)"
                    columnKey="JobRole"
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    theme={theme}
                  />
                )}
                <SortableHeader
                  label="Disabled"
                  columnKey="Disabled"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <th className="py-1 px-2 text-left">Perms</th>
                <th className="py-1 px-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr
                  key={u.UserID}
                  className={`border-b ${
                    theme === "dark"
                      ? "border-gray-600 hover:bg-gray-600"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <td className="py-1 px-2">{u.UserID}</td>
                  <td className="py-1 px-2">{u.FullName}</td>
                  <td className="py-1 px-2">{u.Email}</td>
                  <td className="py-1 px-2">
                    {ROLE_DESCRIPTIONS[u.Role] || u.Role}
                  </td>

                  {showJobRoleUI && (
                    <td className="py-1 px-2">
                      {joinRoles(splitRoles(u.JobRole || "")) || "—"}
                    </td>
                  )}

                  <td className="py-1 px-2">
                    {String(u.Disabled) === "1" ? "Yes" : "No"}
                  </td>

                  <td
                    className="py-1 px-2"
                    dangerouslySetInnerHTML={{
                      __html: formatPermsSummary(u.perms),
                    }}
                  />

                  <td className="py-1 px-2">
                    <button
                      onClick={() => handleEditClick(u)}
                      className="inline-flex items-center px-2 py-1 rounded bg-green-500 hover:bg-green-600 text-white text-xs"
                    >
                      <FontAwesomeIcon icon={faUserEdit} className="mr-1" />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500 dark:text-gray-300">No users found.</p>
      )}

      {/* User Create/Edit Modal */}
      {showUserModal && editingUser && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-40 ${
            theme === "dark"
              ? "bg-black bg-opacity-80"
              : "bg-black bg-opacity-50"
          }`}
        >
          <div
            className={`w-full max-w-lg p-4 rounded-lg shadow-2xl relative ${
              theme === "dark"
                ? "bg-gray-800 text-gray-100"
                : "bg-white text-gray-900"
            } max-h-[85vh] overflow-y-auto`}
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-2 sticky top-0 bg-inherit">
              <h4 className="text-base font-bold">
                {userModalMode === "edit" ? "Edit User" : "Create User"}
              </h4>
              <button
                onClick={handleCancelUser}
                className="p-1 rounded-full text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  UserID
                </label>
                <input
                  name="UserID"
                  value={editingUser.UserID}
                  onChange={handleModalFieldChange}
                  disabled={userModalMode === "edit"}
                  className={`${getInputClasses(theme)} ${
                    userModalMode === "edit"
                      ? "opacity-70 cursor-not-allowed"
                      : ""
                  }`}
                />
                {userModalMode === "edit" && (
                  <div className="text-[11px] text-gray-500 mt-1">
                    UserID is locked while editing.
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  Password (Sec)
                </label>
                <input
                  name="Sec"
                  value={editingUser.Sec}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  Role
                </label>
                <select
                  name="Role"
                  value={editingUser.Role}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                >
                  <option value="A">Admin (A)</option>
                  <option value="P">Pumper (P)</option>
                  <option value="I">Investor (I)</option>
                  <option value="O">Operator (O)</option>
                  <option value="R">Read Only (R)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  Full Name
                </label>
                <input
                  name="FullName"
                  value={editingUser.FullName}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  Email
                </label>
                <input
                  name="Email"
                  value={editingUser.Email}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  Phone
                </label>
                <input
                  name="Phone"
                  value={editingUser.Phone || ""}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>

              {/* Disabled toggle */}
              <div className="flex items-center mt-1">
                <input
                  id="disabledToggle"
                  type="checkbox"
                  checked={editingUser.Disabled === "1"}
                  onChange={(e) => handleDisabledToggle(e.target.checked)}
                  className="mr-1"
                />
                <label
                  htmlFor="disabledToggle"
                  className="text-sm font-semibold"
                >
                  Disabled?
                </label>
              </div>

              {/* JobRole (only show if tenant has jobTypes) */}
              {showJobRoleUI && (
                <>
                  {/* Easier selection: chips + search + select-all/clear (no quick-add, no association) */}
                  <div className="sm:col-span-2">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-semibold">
                        Job Roles
                      </label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            // select all visible filteredRoleChips
                            const merged = joinRoles([
                              ...userRolesArr,
                              ...filteredRoleChips,
                            ]).split(",");
                            setUserRolesArr(splitRoles(merged.join(",")));
                          }}
                          className="text-xs underline"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => setUserRolesArr([])}
                          className="text-xs underline"
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    {/* Search within roles */}
                    <div className="relative mb-2">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className={`absolute left-2 top-2.5 text-xs ${
                          theme === "dark" ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                      <input
                        type="text"
                        placeholder="Filter roles…"
                        value={roleChipSearch}
                        onChange={(e) => setRoleChipSearch(e.target.value)}
                        className={`pl-7 pr-2 py-1 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          theme === "dark"
                            ? "bg-gray-700 text-gray-100 border border-gray-600"
                            : "bg-white text-gray-700 border border-gray-300"
                        }`}
                      />
                    </div>

                    {/* Chip list */}
                    <div className="border rounded p-2 max-h-40 overflow-y-auto">
                      {filteredRoleChips.length ? (
                        filteredRoleChips.map((jr) => {
                          const checked = userRolesArr.includes(jr);
                          return (
                            <Chip
                              key={jr}
                              checked={checked}
                              label={jr}
                              theme={theme}
                              onChange={(on) => {
                                if (on)
                                  setUserRolesArr(
                                    Array.from(new Set([...userRolesArr, jr]))
                                  );
                                else
                                  setUserRolesArr(
                                    userRolesArr.filter((r) => r !== jr)
                                  );
                              }}
                            />
                          );
                        })
                      ) : (
                        <div className="text-xs text-gray-500">
                          No roles match filter.
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold mb-0.5">
                  GroupID
                </label>
                <input
                  name="GroupID"
                  value={editingUser.GroupID || ""}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold mb-0.5">
                  Message
                </label>
                <textarea
                  name="Message"
                  value={editingUser.Message || ""}
                  onChange={handleModalFieldChange}
                  rows={2}
                  className={getInputClasses(theme)}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold mb-0.5">
                  Address
                </label>
                <input
                  name="Address"
                  value={editingUser.Address || ""}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold mb-0.5">
                  City/State/Zip
                </label>
                <input
                  name="CityStateZip"
                  value={editingUser.CityStateZip || ""}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold mb-0.5">
                  Misc
                </label>
                <input
                  name="Misc"
                  value={editingUser.Misc || ""}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>

              {/* Permissions */}
              <div className="sm:col-span-2 border-t pt-2 mt-2">
                <h5 className="text-sm font-bold mb-1">Permissions</h5>

                {/* NOTE: Hide OgPumper perms for now (customer-facing); preserve state + payload */}
                {["ogfieldticket"].map((app) => {
                  const { read, write } = editingUser.perms[app] || {};
                  return (
                    <div
                      key={app}
                      className="flex items-center justify-between mb-1"
                    >
                      <span className="text-xs font-semibold">FieldTicket</span>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={!!read}
                            onChange={(e) =>
                              handlePermCheckbox(app, "read", e.target.checked)
                            }
                            className="mr-1"
                          />
                          Read
                        </label>
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={!!write}
                            onChange={(e) =>
                              handlePermCheckbox(app, "write", e.target.checked)
                            }
                            className="mr-1"
                          />
                          Write
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={handleCancelUser}
                className={`px-3 py-1 rounded text-sm ${
                  theme === "dark"
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                className="px-3 py-1 rounded text-sm bg-blue-500 hover:bg-blue-600 text-white"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tenant JobRole Editor Modal (clientDetails.php only, REPLACE mode for exact set) */}
      {isJobRoleModalOpen && showJobRoleUI && (
        <Modal
          isOpen={isJobRoleModalOpen}
          onRequestClose={() => setIsJobRoleModalOpen(false)}
          contentLabel="Select Job Roles"
          style={modalStyles}
          shouldCloseOnOverlayClick
        >
          <div
            className={`relative rounded-lg shadow-xl p-8 ${
              theme === "dark"
                ? "bg-gray-800 text-white"
                : "bg-white text-gray-800"
            }`}
          >
            <button
              onClick={() => setIsJobRoleModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label="Close"
            >
              ×
            </button>

            <h2 className="text-3xl font-bold mb-8">Select Job Roles</h2>

            <div className="mb-6">
              <label className="block mb-2 font-semibold">Job Type:</label>
              <select
                value={selectedJobTypeId}
                onChange={(e) => setSelectedJobTypeId(e.target.value)}
                className={getInputClasses(theme)}
              >
                {localJobTypes.map((jt) => (
                  <option key={jt.JobTypeID} value={jt.JobTypeID}>
                    {jt.Description || `JobType ${jt.JobTypeID}`}
                  </option>
                ))}
              </select>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const finalRoles = Array.from(
                  new Set(
                    [
                      ...selectedJobRoleIds,
                      ...(newJobRoleName.trim() ? [newJobRoleName.trim()] : []),
                    ].map((r) => r.trim())
                  )
                );

                if (!selectedJobTypeId) {
                  alert("Select a Job Type first.");
                  return;
                }
                if (!companyName) {
                  alert("Missing company.");
                  return;
                }
                try {
                  // REPLACE mode so unchecking removes roles
                  const { data } = await axios.patch(
                    `${baseUrl}/api/clientDetails.php?company=${encodeURIComponent(
                      companyName
                    )}&action=upsertJobRoles&jobtype=${encodeURIComponent(
                      selectedJobTypeId
                    )}&mode=replace`,
                    { jobRoles: finalRoles }
                  );

                  // Update pool from API when available
                  if (data && Array.isArray(data.uniqueJobRoles)) {
                    setJobRoles(
                      Array.from(
                        new Set(
                          data.uniqueJobRoles
                            .map((r) => String(r).trim())
                            .filter(Boolean)
                        )
                      ).sort((a, b) =>
                        a.localeCompare(b, undefined, { sensitivity: "base" })
                      )
                    );
                  } else {
                    // Fallback: ensure pool includes what we kept
                    setJobRoles((prev) =>
                      Array.from(new Set([...prev, ...finalRoles])).sort(
                        (a, b) =>
                          a.localeCompare(b, undefined, { sensitivity: "base" })
                      )
                    );
                  }

                  // Update local JobTypes mirror to reflect exact savedCsv from server (authoritative)
                  const savedCsv = String(
                    data?.savedCsv ?? finalRoles.join(", ")
                  ).trim();
                  setLocalJobTypes((prev) =>
                    prev.map((jt) =>
                      String(jt.JobTypeID) === String(selectedJobTypeId)
                        ? { ...jt, JobRole: savedCsv }
                        : jt
                    )
                  );

                  // Recompute selection from savedCsv so checkboxes reflect truth
                  setSelectedJobRoleIds(splitRoles(savedCsv));

                  // Best-effort parent + pool refresh
                  await fetchTicketTypes();
                  await fetchJobRoles();

                  // Close modal after state sync
                  setIsJobRoleModalOpen(false);
                  setNewJobRoleName("");
                } catch (err) {
                  console.error("Error updating JobRoles:", err);
                }
              }}
            >
              <div className="mb-8">
                <label className="block mb-2 font-semibold">Job Roles:</label>
                <div className="max-h-60 overflow-y-auto border p-2 rounded">
                  {jobRoles.map((role) => (
                    <div key={role} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        value={role}
                        checked={selectedJobRoleIds.includes(role)}
                        onChange={(e) => {
                          const { checked, value } = e.target;
                          setSelectedJobRoleIds((prev) =>
                            checked
                              ? [...prev, value]
                              : prev.filter((r) => r !== value)
                          );
                        }}
                        className="form-checkbox"
                      />
                      <span className="ml-2">{role}</span>
                    </div>
                  ))}
                  {!jobRoles.length && (
                    <div className="text-sm text-gray-500">
                      No roles yet. Add one below.
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-8">
                <label
                  htmlFor="newJobRole"
                  className="block mb-2 font-semibold"
                >
                  Or Create New Job Role:
                </label>
                <input
                  id="newJobRole"
                  value={newJobRoleName}
                  onChange={(e) => setNewJobRoleName(e.target.value)}
                  className={getInputClasses(theme)}
                />
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setIsJobRoleModalOpen(false)}
                  className={`px-6 py-2 rounded ${
                    theme === "dark"
                      ? "bg-gray-700 hover:bg-gray-600"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-6 py-2 text-white ${
                    theme === "dark"
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-blue-500 hover:bg-blue-600"
                  } rounded`}
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ───────── SortableHeader ───────── */
function SortableHeader({
  label,
  columnKey,
  sortBy,
  sortDirection,
  onSort,
  theme,
}) {
  const active = sortBy === columnKey;
  return (
    <th
      onClick={() => onSort(columnKey)}
      className="py-1 px-2 text-left cursor-pointer select-none"
    >
      <span className="flex items-center space-x-1">
        <span>{label}</span>
        {active && (
          <FontAwesomeIcon
            icon={sortDirection === "asc" ? faSortUp : faSortDown}
            className={theme === "dark" ? "text-gray-200" : "text-gray-600"}
          />
        )}
      </span>
    </th>
  );
}

export default UsersTab;
